// utils/resolveLanding.ts
import * as constants from 'resources/constants'
import { Company, Role, User } from 'resources/types'
import SidebarOptions, { SidebarOptionType } from 'shared/modules/sidebar/SidebarOptions'

type FlatPage = {
	title: string
	path?: string
	roles: Role[] // empty means all roles
	hideFromLanding?: boolean
}

function flattenSidebarOptions(
	options: SidebarOptionType[],
	acc: FlatPage[] = [],
): FlatPage[] {
	for (const opt of options) {
		acc.push({
			title: opt.title,
			path: opt.path,
			roles: opt.roles ?? [],
			hideFromLanding: opt.hideFromLanding ?? false, // keep if present
		})
		if (opt.children?.length) flattenSidebarOptions(opt.children, acc)
	}
	return acc
}

// Build once
const FLAT = flattenSidebarOptions(SidebarOptions)

/** Allowed paths for a role (from sidebar), excluding hidden/non-routable */
function allowedPathsForRole(role: Role): Set<string> {
	return new Set(
		FLAT
			.filter(p => !!p.path && !p.hideFromLanding)
			.filter(p => p.roles.length === 0 || p.roles.includes(role))
			.map(p => p.path!), // guaranteed by filter
	)
}

/** App-level defaults per role (edit to taste) */
const DEFAULT_LANDING: Record<Role, string> = {
	[Role.CLIENT]: constants.profilePage,
	[Role.EMPLOYEE]: constants.profilePage,
	[Role.ADMIN]: constants.adminSettingsURL,
	[Role.ACCOUNT_OWNER]: constants.adminSettingsURL,
}

/** True if path is an allowed, internal route for this role */
function isValidLandingPath(path: string | undefined, role: Role): boolean {
	if (!path || !path.startsWith('/')) return false
	return allowedPathsForRole(role).has(path)
}

/**
 * Decide where to send a user after login.
 * Priority: company override → role default → first allowed → '/'.
 */
export function resolveLandingPathForLogin(
	user: Pick<User, 'role'>,
	company?: Company,
): string {
	const role = user.role
	const allowed = allowedPathsForRole(role)

	const companyChoice = company?.roleLanding?.[role]
	if (isValidLandingPath(companyChoice, role)) return companyChoice!

	const roleDefault = DEFAULT_LANDING[role]
	if (isValidLandingPath(roleDefault, role)) return roleDefault

	// Fallback: first allowed route for this role (if any)
	const first = [...allowed][0]
	return first || '/'
}
